import { Classes, Placement } from "@blueprintjs/core";
import {
  OrgRole,
  OrgRoleRequestGatedAction,
  OrgRoleRequestSource,
} from "@hex/common";
import React from "react";
import styled from "styled-components";

import { HexMenuItem } from "../../../hex-components/HexMenuItem";
import { useOrgExplorerRoleEnabled } from "../../../hooks/me/useOrgExplorerRoleEnabled";
import CollectionsGateIcon from "../../../static/illustration/illustration-featuregate-collections.inline.svg";
import ComponentFeatureGateIcon from "../../../static/illustration/illustration-featuregate-components.inline.svg";
import ProjectsGateIcon from "../../../static/illustration/illustration-featuregate-projects.inline.svg";
import { TestSelectors } from "../../../util/testSelectors";
import { RProjectLegacyTag } from "../../common/BetaTag";
import { FeatureGateToolTip } from "../../feature-gate/FeatureGateToolTip";
import {
  CollectionIcon,
  ComponentIcon,
  ExploreIcon,
  ProjectIcon,
  RLanguageIcon,
  ThreadIcon,
  UploadIcon,
} from "../../icons/CustomIcons";
import { RoleRequestTooltip } from "../RoleRequestTooltip";

import {
  CreateMenuLocation,
  useCreateCollectionButton,
  useCreateComponentButton,
  useCreateExplorationButton,
  useCreateProjectButton,
  useCreateThreadButton,
} from "./useCreateAssets";

const MenuItemWrapper = styled.div`
  width: 100%;

  .${Classes.POPOVER_TARGET} {
    width: 100%;
  }

  /* Let the label fill the row so Legacy tag can right-align */
  .${Classes.MENU_ITEM_LABEL} {
    flex: 1;
    min-width: 0;
  }
`;

const CreateMenuItemLabelWithLegacy = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LegacyTagSlot = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;

  && .${Classes.POPOVER_TARGET} {
    display: flex;
  }
`;

export const CreateThreadMenuItem = React.memo(function CreateThreadMenuItem({
  location,
  placement,
}: {
  location: CreateMenuLocation;
  placement?: Placement;
}) {
  const {
    canCreateNewThread,
    createNewThread,
    disabledForRole: createThreadDisabledForRole,
    hasFeatureGate,
    minimumRoleForAccess,
  } = useCreateThreadButton({
    location,
  });
  return (
    <MenuItemWrapper>
      <FeatureGateToolTip
        action="create a new thread"
        disabled={hasFeatureGate}
        featureGate="exploreFromScratch"
        placement={placement}
      >
        <RoleRequestTooltip
          contentText="You do not have permission to create a thread."
          disabled={!createThreadDisabledForRole || !hasFeatureGate}
          gatedAction={OrgRoleRequestGatedAction.CREATE_THREAD}
          placement={placement}
          requestSource={OrgRoleRequestSource.CREATE_PROJECT}
          role={minimumRoleForAccess}
        >
          <HexMenuItem
            disabled={!canCreateNewThread}
            icon={<ThreadIcon />}
            text="New thread"
            onClick={createNewThread}
          />
        </RoleRequestTooltip>
      </FeatureGateToolTip>{" "}
    </MenuItemWrapper>
  );
});

export const CreateProjectMenuItem = React.memo(function CreateProjectMenuItem({
  location,
  placement,
}: {
  location: CreateMenuLocation;
  placement?: Placement;
}) {
  const {
    createNewProject,
    disabledForRole,
    newProjectDisabled,
    projectLimit,
    projectLimitEnforced,
  } = useCreateProjectButton({
    location,
  });
  return (
    <MenuItemWrapper>
      <FeatureGateToolTip
        descriptionOverride="Unlock unlimited projects with a paid plan."
        disabled={!projectLimitEnforced}
        featureGate="projectLimit"
        icon={<ProjectsGateIcon />}
        placement={placement}
        titleOverride={`Your workspace has reached the ${projectLimit} project limit.`}
      >
        <RoleRequestTooltip
          contentText="You do not have permission to create a project."
          disabled={!disabledForRole || !!projectLimitEnforced}
          gatedAction={OrgRoleRequestGatedAction.CREATE_PROJECT}
          placement={placement}
          requestSource={OrgRoleRequestSource.CREATE_PROJECT}
          role={OrgRole.EDITOR}
        >
          <HexMenuItem
            data-test={TestSelectors.CREATE_PROJECT}
            disabled={newProjectDisabled}
            icon={<ProjectIcon />}
            text="New project"
            onClick={createNewProject}
          />
        </RoleRequestTooltip>
      </FeatureGateToolTip>{" "}
    </MenuItemWrapper>
  );
});

export const CreateComponentMenuItem = React.memo(
  function CreateComponentMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const {
      createPythonComponent,
      disabledForRole,
      hasFeatureGate: componentsFeatureGate,
      newComponentDisabled,
    } = useCreateComponentButton({
      location,
    });
    return (
      <MenuItemWrapper>
        <FeatureGateToolTip
          descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
          disabled={componentsFeatureGate}
          featureGate="components"
          icon={<ComponentFeatureGateIcon />}
          placement={placement}
          titleOverride="Upgrade plan to enable components"
        >
          <RoleRequestTooltip
            contentText="You do not have permission to create a component."
            disabled={!disabledForRole || !componentsFeatureGate}
            gatedAction={OrgRoleRequestGatedAction.CREATE_COMPONENT}
            placement={placement}
            requestSource={OrgRoleRequestSource.CREATE_COMPONENT}
            role={OrgRole.EDITOR}
          >
            <HexMenuItem
              data-test={TestSelectors.CREATE_COMPONENT}
              disabled={newComponentDisabled}
              icon={<ComponentIcon />}
              text="New component"
              onClick={createPythonComponent}
            />
          </RoleRequestTooltip>
        </FeatureGateToolTip>
      </MenuItemWrapper>
    );
  },
);

export const CreateRProjectMenuItem = React.memo(
  function CreateRProjectMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const {
      createRProject,
      disabledForRole,
      newProjectDisabled,
      projectLimit,
      projectLimitEnforced,
    } = useCreateProjectButton({
      location,
    });
    const menuItemLabel = (
      <CreateMenuItemLabelWithLegacy>
        <span>New R project</span>
        <LegacyTagSlot>
          <RProjectLegacyTag />
        </LegacyTagSlot>
      </CreateMenuItemLabelWithLegacy>
    );
    return (
      <MenuItemWrapper>
        <FeatureGateToolTip
          descriptionOverride="Unlock unlimited projects with a paid plan."
          disabled={!projectLimitEnforced}
          featureGate="projectLimit"
          icon={<ProjectsGateIcon />}
          placement={placement}
          titleOverride={`Your workspace has reached the ${projectLimit} project limit.`}
        >
          <RoleRequestTooltip
            contentText="You do not have permission to create a project."
            disabled={!disabledForRole || !!projectLimitEnforced}
            gatedAction={OrgRoleRequestGatedAction.CREATE_PROJECT}
            placement={placement}
            requestSource={OrgRoleRequestSource.CREATE_PROJECT}
            role={OrgRole.EDITOR}
          >
            <HexMenuItem
              data-test={TestSelectors.CREATE_R_PROJECT}
              disabled={newProjectDisabled}
              icon={<RLanguageIcon />}
              text={menuItemLabel}
              onClick={createRProject}
            />
          </RoleRequestTooltip>
        </FeatureGateToolTip>
      </MenuItemWrapper>
    );
  },
);

export const CreateRComponentMenuItem = React.memo(
  function CreateRComponentMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const {
      createRComponent,
      disabledForRole,
      hasFeatureGate: componentsFeatureGate,
      newComponentDisabled,
    } = useCreateComponentButton({
      location,
    });
    const menuItemLabel = (
      <CreateMenuItemLabelWithLegacy>
        <span>New R component</span>
        <LegacyTagSlot>
          <RProjectLegacyTag />
        </LegacyTagSlot>
      </CreateMenuItemLabelWithLegacy>
    );
    return (
      <MenuItemWrapper>
        <FeatureGateToolTip
          descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
          disabled={componentsFeatureGate}
          featureGate="components"
          icon={<ComponentFeatureGateIcon />}
          placement={placement}
          titleOverride="Upgrade plan to enable components"
        >
          <RoleRequestTooltip
            contentText="You do not have permission to create a component."
            disabled={!disabledForRole || !componentsFeatureGate}
            gatedAction={OrgRoleRequestGatedAction.CREATE_COMPONENT}
            placement={placement}
            requestSource={OrgRoleRequestSource.CREATE_COMPONENT}
            role={OrgRole.EDITOR}
          >
            <HexMenuItem
              data-test={TestSelectors.CREATE_R_COMPONENT}
              disabled={newComponentDisabled}
              icon={<RLanguageIcon />}
              text={menuItemLabel}
              onClick={createRComponent}
            />
          </RoleRequestTooltip>
        </FeatureGateToolTip>
      </MenuItemWrapper>
    );
  },
);

export const ImportProjectMenuItem = React.memo(function ImportProjectMenuItem({
  location,
  placement,
}: {
  location: CreateMenuLocation;
  placement?: Placement;
}) {
  const {
    disabledForRole,
    newProjectDisabled,
    openImportDialog,
    projectLimit,
    projectLimitEnforced,
  } = useCreateProjectButton({
    location,
  });
  return (
    <MenuItemWrapper>
      <FeatureGateToolTip
        descriptionOverride="Unlock unlimited projects with a paid plan."
        disabled={!projectLimitEnforced}
        featureGate="projectLimit"
        icon={<ProjectsGateIcon />}
        placement={placement}
        titleOverride={`Your workspace has reached the ${projectLimit} project limit.`}
      >
        <RoleRequestTooltip
          contentText="You do not have permission to create a project."
          disabled={!disabledForRole || !!projectLimitEnforced}
          gatedAction={OrgRoleRequestGatedAction.CREATE_PROJECT}
          placement={placement}
          requestSource={OrgRoleRequestSource.CREATE_PROJECT}
          role={OrgRole.EDITOR}
        >
          <HexMenuItem
            data-test={TestSelectors.IMPORT_PROJECT}
            disabled={newProjectDisabled}
            icon={<UploadIcon />}
            text="Upload project"
            onClick={openImportDialog}
          />
        </RoleRequestTooltip>
      </FeatureGateToolTip>
    </MenuItemWrapper>
  );
});

export const ImportComponentMenuItem = React.memo(
  function ImportComponentMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const {
      disabledForRole,
      hasFeatureGate: componentsFeatureGate,
      newComponentDisabled,
      openComponentImportDialog,
    } = useCreateComponentButton({
      location,
    });
    return (
      <MenuItemWrapper>
        <FeatureGateToolTip
          descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
          disabled={componentsFeatureGate}
          featureGate="components"
          icon={<ComponentFeatureGateIcon />}
          placement={placement}
          titleOverride="Upgrade plan to enable components"
        >
          <RoleRequestTooltip
            contentText="You do not have permission to create a component."
            disabled={!disabledForRole || !componentsFeatureGate}
            gatedAction={OrgRoleRequestGatedAction.CREATE_COMPONENT}
            placement={placement}
            requestSource={OrgRoleRequestSource.CREATE_COMPONENT}
            role={OrgRole.EDITOR}
          >
            <HexMenuItem
              disabled={newComponentDisabled}
              icon={<UploadIcon />}
              text="Upload component"
              onClick={openComponentImportDialog}
            />
          </RoleRequestTooltip>
        </FeatureGateToolTip>
      </MenuItemWrapper>
    );
  },
);

export const CreateCollectionMenuItem = React.memo(
  function CreateCollectionMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const {
      disabledForRole,
      hasFeatureGate: collectionsFeatureGate,
      newCollectionDisabled,
      openNewCollectionDialog,
    } = useCreateCollectionButton({
      location,
    });
    return (
      <MenuItemWrapper>
        <FeatureGateToolTip
          descriptionOverride="Collections give you the ability to organize and share your projects."
          disabled={collectionsFeatureGate}
          featureGate="collections"
          icon={<CollectionsGateIcon />}
          placement={placement}
          titleOverride="Upgrade plan to enable collections"
        >
          <RoleRequestTooltip
            contentText="You do not have permission to create a collection."
            disabled={!disabledForRole || !collectionsFeatureGate}
            gatedAction={OrgRoleRequestGatedAction.CREATE_COLLECTION}
            placement={placement}
            requestSource={OrgRoleRequestSource.CREATE_COLLECTION}
            role={OrgRole.EDITOR}
          >
            <HexMenuItem
              disabled={newCollectionDisabled}
              icon={<CollectionIcon />}
              text="New collection"
              onClick={openNewCollectionDialog}
            />
          </RoleRequestTooltip>
        </FeatureGateToolTip>
      </MenuItemWrapper>
    );
  },
);

export const CreateExplorationMenuItem = React.memo(
  function CreateExplorationMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const exploreRoleEnabled = useOrgExplorerRoleEnabled();
    const {
      createNewExplore,
      disabledForRole,
      hasFeatureGate: exploreFromScratchFeatureGate,
      newExplorationDisabled,
    } = useCreateExplorationButton({
      location,
    });
    return (
      <MenuItemWrapper>
        <FeatureGateToolTip
          action="explore outside of a published app"
          disabled={exploreFromScratchFeatureGate}
          featureGate="exploreFromScratch"
          placement={placement}
        >
          <RoleRequestTooltip
            contentText="You do not have permission to create an exploration."
            disabled={!disabledForRole || !exploreFromScratchFeatureGate}
            gatedAction={OrgRoleRequestGatedAction.EXPLORE}
            placement={placement}
            requestSource={OrgRoleRequestSource.CREATE_EXPLORE}
            role={exploreRoleEnabled ? OrgRole.EXPLORER : OrgRole.EDITOR}
          >
            <HexMenuItem
              data-test={TestSelectors.NEW_EXPLORATION}
              disabled={newExplorationDisabled}
              icon={<ExploreIcon />}
              text="New exploration"
              onClick={createNewExplore}
            />
          </RoleRequestTooltip>
        </FeatureGateToolTip>
      </MenuItemWrapper>
    );
  },
);
